import {
  Box,
  Button,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  GridActionsCellItem,
  GridCellModes,
  GridCellModesModel,
  GridCellParams,
  GridColDef,
  GridRowModel,
} from "@mui/x-data-grid-pro";
import React, { useCallback, useEffect, useState } from "react";
import { DataGridComponent } from "../common/DataGridComponent";

import FileCopyIcon from "@mui/icons-material/FileCopy";
import { useCodelist } from "../../hooks/useCodelist";
import { codelistUtils } from "../../utils/codelistUtils";
import { useSnackbar } from "../../hooks/useSnackbar";

const AddReasonForm: React.FunctionComponent<any> = (props: any) => {
  const { showError } = useSnackbar();

  const { getCodelistData, codelistData } = useCodelist();

  useEffect(() => {
    getCodelistData({ data: { code_list_codes: ["EDIT_REASON"] } });
  }, []);

  const columns: GridColDef[] = [
    {
      field: "field",
      headerName: "Field",
      width: 150,
    },
    {
      field: "oldData",
      headerName: "Old Data",
      minWidth: 150,
      flex: 1,
    },
    {
      field: "newData",
      headerName: "New Data",
      width: 150,
    },
    {
      field: "reason",
      headerClassName: "editableColumnHeader",
      renderHeader: () => (
        <strong>
          {"Reason "} <span className="textRed">*</span>
        </strong>
      ),
      type: "singleSelect",
      editable: true,
      valueOptions:
        codelistData?.length > 0 &&
        codelistUtils(codelistData, "EDIT_REASON")?.codelistCodeValues?.map(
          (item: any) => item.code_list_value
        ),
      width: 190,
      renderCell: (params: any) => params.row.reason,
      // renderCell:(params: any) => {
      //     return(
      //     <FormControl fullWidth size="small" variant="standard" sx={{ mt: 1 }}>
      //       <Select
      //         id='reason'
      //         value={params.row.reason}
      //         className={  !params?.row?.reason || params?.row?.reason?.length <= 0 ? "errorBox" : undefined}
      //       >
      //         {codelistData?.length>0 && codelistUtils(codelistData,'EDIT_REASON')?.codelistCodeValues?.
      //         map((item:any,index:number)=>
      //             <MenuItem value={item?.code_list_value} key={index}>{item?.code_list_value}</MenuItem>
      //         )}

      //       </Select>
      //     </FormControl>
      //   )},
    },
    {
      field: "comments",
      headerClassName: "editableColumnHeader",
      editable: true,
      renderHeader: () => (
        <strong>
          {"Comment "} <span className="textRed">*</span>
        </strong>
      ),
      minWidth: 150,
      flex: 1.5,
      renderCell: (params: any) => (
        <TextField
          className={
            params?.row?.comments?.length <= 0 ? "errorBox" : undefined
          }
          variant="standard"
          sx={{ mt: 1 }}
          value={params.row.comments}
        />
      ),
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 80,
      cellClassName: "actions",
      getActions: ({ id }) => {
        return [
          <Tooltip
            key={id}
            title="Reason and Comments of this row will be copied to rest of the rows"
            placement="left"
          >
            <GridActionsCellItem
              icon={<FileCopyIcon fontSize="small" />}
              label="Copy"
              onClick={() => handleCopyAll(id)}
              color="primary"
            />
          </Tooltip>,
        ];
      },
    },
  ];
  const [rows, setRows] = useState(props?.editedData);
  const processRowUpdate = (newRow: GridRowModel) => {
    const updatedRow = { ...newRow, isNew: false };
    setRows(rows.map((row: any) => (row.id === newRow.id ? updatedRow : row)));
    return updatedRow;
  };

  const [updatedDataObj, setUpdatedDataObj] = useState([]);

  useEffect(() => {
    const temp = rows.map((item: any) => {
      return {
        ...item,
        reason_code: "EDIT_REASON",
        reason_value_code:
          codelistData?.length > 0 &&
          codelistUtils(codelistData, "EDIT_REASON")?.codelistCodeValues?.find(
            (type: any) => type.code_list_value == item.reason
          )?.code_list_value_code,
      };
    });
    temp.forEach((e: any) => {
      delete e.field;
      delete e.isNew;
      delete e.newData;
      delete e.oldData;
      e.field_name = e.id;
    });
    setUpdatedDataObj(temp);
  }, [rows, codelistData]);

  const handleSave = () => {
    const missingData = updatedDataObj?.find(
      (item: any) => !item.reason || !item.comments
    );
    if (missingData) {
      showError("Reason/Comment is required");
      return;
    }
    props.handleAddReason(updatedDataObj);
  };

  const handleCopyAll = (id: any) => {
    const copyRow = rows.find((item: { id: string }) => item.id == id);
    const updatedRows = rows.map((item: any) => {
      return { ...item, reason: copyRow?.reason, comments: copyRow?.comments };
    });
    setRows(updatedRows);
  };

  // Single Click Edit

  const [cellModesModel, setCellModesModel] = useState<GridCellModesModel>({});

  const handleCellClick = useCallback(
    (params: GridCellParams, event: React.MouseEvent) => {
      if (!params.isEditable) {
        return;
      }

      // Ignore portal
      if (
        (event.target as any).nodeType === 1 &&
        !event.currentTarget.contains(event.target as Element)
      ) {
        return;
      }

      setCellModesModel((prevModel) => {
        return {
          // Revert the mode of the other cells from other rows
          ...Object.keys(prevModel).reduce(
            (acc, id) => ({
              ...acc,
              [id]: Object.keys(prevModel[id]).reduce(
                (acc2, field) => ({
                  ...acc2,
                  [field]: { mode: GridCellModes.View },
                }),
                {}
              ),
            }),
            {}
          ),
          [params.id]: {
            // Revert the mode of other cells in the same row
            ...Object.keys(prevModel[params.id] || {}).reduce(
              (acc, field) => ({
                ...acc,
                [field]: { mode: GridCellModes.View },
              }),
              {}
            ),
            [params.field]: { mode: GridCellModes.Edit },
          },
        };
      });
    },
    []
  );

  const handleCellModesModelChange = useCallback(
    (newModel: GridCellModesModel) => {
      setCellModesModel(newModel);
    },
    []
  );
  return (
    <>
      <Box className="addReasonMainWrap">
        <Stack direction="row" columnGap={2} justifyContent="space-between">
          <Typography variant="h6" gutterBottom>
            Add Reason
          </Typography>
          <Typography variant="caption">
            <span className="legend EditableCol">&nbsp;</span> Editable columns
          </Typography>
        </Stack>
        <Box
          sx={{
            width: "100%",
            maxHeight: "calc(54vh - 90px)",
            overflowY: "auto",
            mr: -1.5,
            pr: 0.5,
            mb: 1,
          }}
        >
          <DataGridComponent
            columns={columns}
            autoHeight
            rows={rows}
            sx={{
              "& .MuiDataGrid-toolbarContainer, & .MuiTablePagination-root": {
                display: "none",
              },
            }}
            // editMode="row"
            processRowUpdate={processRowUpdate}
            cellModesModel={cellModesModel}
            onCellModesModelChange={handleCellModesModelChange}
            onCellClick={handleCellClick}
            // onCellEditStop={(
            //   params: GridCellEditStopParams,
            //   event: MuiEvent
            // ) => {
            //   if (params.reason === GridCellEditStopReasons.cellFocusOut) {
            //     console.debug(params);
            //   }
            // }}
          />
        </Box>
        <Box pb={1} width="100%">
          <Stack direction="row" justifyContent="flex-end" spacing={2} mr={-2}>
            <Button className="grey-btn" onClick={props?.toggleDrawer(false)}>
              Cancel
            </Button>
            <Button onClick={() => handleSave()}>Save</Button>
          </Stack>
        </Box>
      </Box>
    </>
  );
};

export default AddReasonForm;
